import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, X, Phone, Mail, PhoneCall } from "lucide-react";

export default function Hero() {
  const navigate = useNavigate();
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [voice, setVoice] = useState("f");

  // null | "contact"
  const [phase, setPhase] = useState(null);

  const closeModal = () => {
    setPhase(null);
    setPhone("");
    setEmail("");
    setPhoneError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!website.trim()) return;
    setPhase("contact");
  };

  // Normalizza un numero italiano: tiene solo le cifre, toglie 0039 / +39 / 39, ritorna il numero locale.
  const normalizePhone = (raw) => {
    let d = (raw || "").replace(/[^\d+]/g, "");
    d = d.replace(/^00/, "+");
    if (d.startsWith("+")) d = d.slice(1);
    if (d.startsWith("39")) d = d.slice(2);
    return d;
  };

  const handleCall = (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      setPhoneError("Il numero di telefono è obbligatorio");
      return;
    }
    const local = normalizePhone(phone);
    // Cellulare italiano: 10 cifre, inizia con 3 (es. 333 1234567)
    if (!/^3\d{9}$/.test(local)) {
      setPhoneError("Numero non valido: inserisci un cellulare italiano a 10 cifre (es. 333 1234567).");
      return;
    }
    setPhoneError("");
    const e164 = "+39" + local; // numero pulito e completo, garantito
    navigate("/chiama", { state: { website: website.trim(), phone: e164, email: email.trim(), voice } });
  };

  return (
    <section id="hero" className="relative pt-16 pb-24 md:pt-24 md:pb-36 overflow-hidden grain-bg" data-testid="hero-section">
      <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-20 pointer-events-none hidden md:block">
        <div className="sound-wave" style={{ height: 280, gap: 8 }}>
          {Array.from({ length: 12 }).map((_, i) => (
            <span key={i} style={{ width: 8 }}></span>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-8 relative">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-bg2 border border-brand-line mb-8">
            <span className="w-2 h-2 rounded-full bg-brand-terra animate-pulse"></span>
            <span className="text-xs text-brand-inkSoft tracking-wide">24Vox · la tua voce, sempre</span>
          </div>

          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] text-brand-ink mb-6" data-testid="hero-headline">
            L'assistente vocale<br />
            che <span className="text-brand-terra italic">risponde</span><br />
            quando tu non puoi.
          </h1>

          <p className="text-lg md:text-xl text-brand-inkSoft mb-10 max-w-xl leading-relaxed" data-testid="hero-subheadline">
            In 5 minuti, la tua AI personalizzata sul tuo sito. Senza commerciali, senza call discovery, senza contratti.
          </p>

          <form onSubmit={handleSubmit} className="bg-white border border-brand-line rounded-2xl p-6 md:p-8 shadow-sm max-w-2xl" data-testid="hero-form">
            <div className="space-y-4">
              <div>
                <label htmlFor="website" className="block text-xs font-medium text-brand-inkSoft mb-2 uppercase tracking-wide">
                  Sito Web o Social
                </label>
                <input
                  id="website"
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="es: tenutaciminata.it o instagram.com/nometuo"
                  className="input-clean w-full px-4 py-3 rounded-lg text-base"
                  required
                  data-testid="website-input"
                  autoComplete="url"
                />
              </div>
              <button
                type="submit"
                className="btn-olive w-full px-6 py-4 rounded-lg font-semibold text-base flex items-center justify-center gap-2"
                data-testid="hero-submit-button"
              >
                Prova subito gratis
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-brand-inkSoft text-center mt-4 leading-relaxed">
              Nessuna carta, nessun obbligo.
            </p>
          </form>
        </div>
      </div>

      {/* ---- MODAL CONTATTI ---- */}
      {phase === "contact" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 relative" data-testid="hero-modal">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-brand-inkSoft hover:text-brand-ink transition"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-brand-olive/10 flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-brand-olive" />
              </div>
              <h2 className="font-heading text-xl text-brand-ink">Dove ti chiamiamo?</h2>
            </div>
            <p className="text-sm text-brand-inkSoft mb-6">
              Entro 30 secondi ricevi la chiamata dal tuo assistente AI personalizzato su <strong className="text-brand-ink">{website.trim()}</strong>.
            </p>

            <form onSubmit={handleCall} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-brand-inkSoft mb-2 uppercase tracking-wide">
                  Numero di telefono <span className="text-brand-terra">*</span>
                </label>
                <div className="flex items-center border border-brand-line rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-brand-terra/40">
                  <PhoneCall className="w-4 h-4 text-brand-inkSoft ml-3 shrink-0" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); setPhoneError(""); }}
                    placeholder="+39 333 1234567"
                    className="flex-1 px-3 py-3 text-sm outline-none bg-transparent"
                    autoFocus
                    autoComplete="tel"
                  />
                </div>
                {phoneError && <p className="text-xs text-red-500 mt-1">{phoneError}</p>}
                <p className="text-xs text-brand-inkSoft mt-1">Formato internazionale: +39 per Italia</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-brand-inkSoft mb-2 uppercase tracking-wide">
                  Email <span className="text-brand-inkSoft font-normal">(opzionale)</span>
                </label>
                <div className="flex items-center border border-brand-line rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-brand-terra/40">
                  <Mail className="w-4 h-4 text-brand-inkSoft ml-3 shrink-0" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="mario@esempio.it"
                    className="flex-1 px-3 py-3 text-sm outline-none bg-transparent"
                    autoComplete="email"
                  />
                </div>
                <p className="text-xs text-brand-inkSoft mt-1">Ti mandiamo il riassunto della chiamata</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-brand-inkSoft mb-2 uppercase tracking-wide">
                  Voce dell'assistente
                </label>
                <div className="flex gap-2">
                  {[{ value: "f", label: "Femminile" }, { value: "m", label: "Maschile" }].map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setVoice(value)}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition ${
                        voice === value
                          ? "bg-brand-olive text-white border-brand-olive"
                          : "border-brand-line text-brand-inkSoft hover:text-brand-ink hover:bg-brand-bg"
                      }`}
                    >
                      {value === "f" ? "👩" : "👨"} {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-3 rounded-lg border border-brand-line text-sm text-brand-inkSoft hover:text-brand-ink hover:bg-brand-bg transition font-medium"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 rounded-lg btn-olive text-sm font-semibold flex items-center justify-center gap-2"
                  data-testid="call-confirm-button"
                >
                  <Phone className="w-4 h-4" />
                  Chiamami adesso
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
